import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserContext } from "../../../shared/context/userContext";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  mapProgressBuyNowToRadioSelection,
} from "../../../shared/utils/onboardingProgressUtil";
import styles from "./OnboardingTierThreeBuyNow.module.scss";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";

const OnboardingTierThreeBuyNow = ({ savedBuyNowPreference }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const isFordBrand = IS_FORD_BRAND(user?.brand);

  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;

  const userId = user?.userId || user?.commonName;

  const [selectedOption, setSelectedOption] = useState("");
  const [savedApiSelection, setSavedApiSelection] = useState(() =>
    mapProgressBuyNowToRadioSelection(savedBuyNowPreference)
  );
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    const mappedSelection = mapProgressBuyNowToRadioSelection(
      savedBuyNowPreference
    );

    setSelectedOption(mappedSelection);
    setSavedApiSelection(mappedSelection);
  }, [savedBuyNowPreference]);

  const saveTierThreeBuyNowMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.saveTierThreeBuyNowPreference(
        dealerCommonId,
        payload,
        user?.token
      ),

    onSuccess: async (_data, variables) => {
      setSaveError(false);
      setSaveSuccess(false);

      await queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
        exact: true,
        refetchType: "active",
      });

      setSavedApiSelection(variables.buyNowPreference);
      setSaveSuccess(true);
    },

    onError: () => {
      setSaveSuccess(false);
      setSaveError(true);
    },
  });

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setSaveSuccess(false);
    setSaveError(false);
  };

  const handleSave = () => {
    if (
      !selectedOption ||
      !dealerCommonId ||
      saveTierThreeBuyNowMutation.isPending ||
      selectedOption === savedApiSelection
    ) {
      if (!dealerCommonId) {
        setSaveSuccess(false);
        setSaveError(true);
      }

      return;
    }

    setSaveSuccess(false);
    setSaveError(false);

    saveTierThreeBuyNowMutation.mutate({
      buyNowPreference: selectedOption,
      updatedBy: userId,
    });
  };

  const isSubmitting = saveTierThreeBuyNowMutation.isPending;
  const hasSelectedOption = Boolean(selectedOption);

  const isDisplayedSelectionSaved =
    hasSelectedOption && selectedOption === savedApiSelection;

  const isSaveDisabled =
    !hasSelectedOption || isSubmitting || isDisplayedSelectionSaved;

  const saveButtonLabel = isDisplayedSelectionSaved
    ? t("DealerOnboarding.content.tierThreeBuyNow.savedButtonLabel", "Saved")
    : t("DealerOnboarding.content.tierThreeBuyNow.saveButtonLabel", "Save");

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle
        testId="tier-three-buy-now-title"
        className="tw-mb-6"
      >
        {t(
          "DealerOnboarding.content.tierThreeBuyNow.title",
          "Tier 3 Buy Now Button"
        )}
      </OnboardingPageTitle>

      <Box className={styles.contentContainer}>
        {saveSuccess && (
          <EMPConfirmationBanner
            variant="success"
            primaryText={t(
              "DealerOnboarding.content.tierThreeBuyNow.saveSuccess",
              "Your choice has been saved"
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.tierThreeBuyNow.closeSuccessBanner",
              "Close confirmation banner"
            )}
            testId="tier-three-buy-now-save-success"
          />
        )}

        {saveError && (
          <EMPConfirmationBanner
            variant="error"
            primaryText={t(
              "DealerOnboarding.content.tierThreeBuyNow.saveError",
              "Unable to save your choice. Please try again."
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.tierThreeBuyNow.closeErrorBanner",
              "Close error banner"
            )}
            testId="tier-three-buy-now-save-error"
          />
        )}

        <Box className={styles.descriptionContainer}>
          <Typography
            component="p"
            className={styles.bodyText}
            data-testid="tier-three-buy-now-description-one"
          >
            {t("DealerOnboarding.content.tierThreeBuyNow.descriptionOne")}
          </Typography>

          <Typography
            component="p"
            className={styles.bodyText}
            data-testid="tier-three-buy-now-description-two"
          >
            {t("DealerOnboarding.content.tierThreeBuyNow.descriptionTwo")}
          </Typography>
        </Box>

        <Box className={styles.selectionContainer}>
          <Typography
            component="p"
            className={styles.question}
            data-testid="tier-three-buy-now-question"
          >
            {t("DealerOnboarding.content.tierThreeBuyNow.question")}
          </Typography>

          <FormControl component="fieldset">
            <RadioGroup
              name="tier-three-buy-now"
              value={selectedOption}
              onChange={handleOptionChange}
              data-testid="tier-three-buy-now-radio-group"
              className={styles.radioGroup}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio className={styles.radioButton} disableRipple />}
                label={t(
                  "DealerOnboarding.content.tierThreeBuyNow.radioYes",
                  "Yes"
                )}
                data-testid="tier-three-buy-now-radio-yes"
                className={styles.radioLabel}
              />

              <FormControlLabel
                value="No"
                control={<Radio className={styles.radioButton} disableRipple />}
                label={t(
                  "DealerOnboarding.content.tierThreeBuyNow.radioNo",
                  "No"
                )}
                data-testid="tier-three-buy-now-radio-no"
                className={styles.radioLabel}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            disabled={isSaveDisabled}
            onClick={handleSave}
            isFord={isFordBrand}
            testId="tier-three-buy-now-save-button"
            className={styles.saveButton}
          >
            {saveButtonLabel}
          </EMPButton>
        </Box>
      </Box>
    </div>
  );
};

OnboardingTierThreeBuyNow.propTypes = {
  savedBuyNowPreference: PropTypes.string,
};

OnboardingTierThreeBuyNow.defaultProps = {
  savedBuyNowPreference: "",
};

export default OnboardingTierThreeBuyNow;
